import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/pages/Dashboard";
import { Panel } from "@/pages/Billing";

const cell = "h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none transition-colors focus:border-sky-500/60";
export const usd = (v) => `$${Number(v || 0).toFixed(v && Math.abs(v) < 1 ? 4 : 2)}`;
const num = (v) => Number(v || 0).toLocaleString();

function PriceRow({ row, idx, onSaved }) {
  const [p, setP] = useState({ input: row.input_per_m ?? "", output: row.output_per_m ?? "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const i = Number(p.input), o = Number(p.output);
    if (p.input === "" || p.output === "" || !Number.isFinite(i) || !Number.isFinite(o) || i < 0 || o < 0 || i > 1000 || o > 1000)
      return setError("Enter prices from 0 to 1000 USD per 1M tokens");
    setError("");
    setBusy(true);
    try {
      await api.put("/admin/ai-prices", { model: row.model, input_per_m: i, output_per_m: o });
      toast.success(`${row.model} prices saved. Used for new readings.`);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr data-testid={`ai-price-row-${idx}`} className="border-t border-slate-800/70 align-top text-sm">
      <td className="px-6 py-3 pt-5 font-mono-num text-slate-100" data-testid={`ai-price-model-${idx}`}>
        {row.model}
        {row.primary && <span className="ml-2 rounded-full border border-emerald-500/40 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300" data-testid={`ai-price-primary-${idx}`}>primary</span>}
        {row.fallback && <span className="ml-2 rounded-full border border-sky-500/40 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-300">fallback</span>}
      </td>
      {["input", "output"].map((k) => (
        <td key={k} className="px-3 py-3"><div className="relative"><span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
          <input data-testid={`ai-price-${k}-input-${idx}`} value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} inputMode="decimal" className={`${cell} w-28 pl-6 text-right font-mono-num`} /></div></td>
      ))}
      <td className="px-6 py-3 text-right">
        <button data-testid={`ai-price-save-button-${idx}`} onClick={save} disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-full bg-sky-500 px-4 text-sm font-semibold text-slate-950 transition-colors hover:bg-sky-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        {error && <p data-testid={`ai-price-error-${idx}`} className="mt-2 text-left text-xs text-red-300">{error}</p>}
      </td>
    </tr>
  );
}

function PriceTable() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/ai-prices").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px]" data-testid="ai-prices-table">
        <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="px-6 py-3">Model</th><th className="px-3 py-3">Input / 1M tokens</th><th className="px-3 py-3">Output / 1M tokens</th><th className="px-6 py-3" /></tr></thead>
        <tbody>{rows?.map((r, i) => <PriceRow key={`${r.model}-${r.input_per_m}-${r.output_per_m}`} row={r} idx={i} onSaved={load} />)}</tbody>
      </table>
      {rows === null && <div className="h-24 animate-pulse bg-slate-900/40" />}
    </div>
  );
}

const HEAD = ["Date", "Company", "Files", "Pages", "How read", "Models", "Tokens in / out", "AI cost", "Credits"];

export function ReadingsTable({ companyId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/readings", { params: companyId ? { company_id: companyId } : {} }).then((r) => setRows(r.data))
      .catch((e) => { toast.error(errorMessage(e)); setRows([]); });
  }, [companyId]);
  if (rows === null) return <div className="h-32 animate-pulse bg-slate-900/40" />;
  if (!rows.length) return <p data-testid="readings-empty" className="px-6 py-10 text-center text-sm text-slate-500">No readings with recorded AI cost yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1100px]" data-testid="readings-table">
        <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {HEAD.map((h, i) => <th key={h} className={`px-4 py-3 ${i === 3 || i >= 6 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.id} data-testid={`reading-row-${i}`} className="border-t border-slate-800/70 text-sm">
            <td className="px-4 py-3 text-slate-400">{fmtDate(r.date)}</td>
            <td className="px-4 py-3 text-slate-100">{r.company || "—"}</td>
            <td className="max-w-[260px] truncate px-4 py-3 text-slate-300" title={r.files.join(", ")}>{r.files.join(", ") || "—"}</td>
            <td className="px-4 py-3 text-right font-mono-num text-slate-300">{r.pages}</td>
            <td className="px-4 py-3" data-testid={`reading-method-${i}`}>
              <div className="flex flex-wrap gap-1">{r.methods.map((m) => (
                <span key={m} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${m === "Read without AI" || m === "Reused result" ? "border-emerald-500/40 text-emerald-300" : m === "Claude fallback" ? "border-sky-500/40 text-sky-300" : "border-slate-600 text-slate-300"}`}>{m}</span>))}</div>
            </td>
            <td className="px-4 py-3 text-xs text-slate-400">{r.models.join(", ") || "—"}</td>
            <td className="px-4 py-3 text-right font-mono-num text-xs text-slate-400" data-testid={`reading-tokens-${i}`}>{num(r.input_tokens)} / {num(r.output_tokens)}</td>
            <td className="px-4 py-3 text-right font-mono-num text-amber-200" data-testid={`reading-cost-${i}`}>
              {usd(r.ai_cost)}{r.estimated && <span data-testid={`reading-estimated-${i}`} className="ml-1.5 rounded border border-amber-500/40 px-1 text-[10px] uppercase text-amber-300" title="Provider returned no token counts; estimated from prompt, response and pages">est.</span>}
            </td>
            <td className="px-4 py-3 text-right font-mono-num text-slate-300" data-testid={`reading-credits-${i}`}>{r.credits_charged}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-gold-ivory p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p data-testid={testId} className="mt-2 font-mono-num text-2xl font-bold text-slate-50">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function AiTotals() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setO(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!o) return <div className="h-28 animate-pulse rounded-2xl bg-slate-900/40" />;
  return (
    <div data-testid="admin-ai-totals" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Stat label="AI cost" value={usd(o.ai_cost)} sub={`${usd(o.ai_cost_month)} this month`} testId="overview-ai-cost" />
      <Stat label="Avg AI cost / reading" value={usd(o.avg_ai_cost)} sub={`${usd(o.avg_ai_cost_month)} this month · ${o.costed_readings} costed readings`} testId="overview-avg-ai-cost" />
      <Stat label="Drawings read" value={o.drawings_read} sub={`${o.drawings_read_month} this month`} testId="overview-drawings-read" />
      <Stat label="Readings at $0 AI cost" value={o.no_ai_readings + o.reused_readings} sub={`${o.no_ai_readings} read without AI · ${o.reused_readings} reused`} testId="overview-free-readings" />
    </div>
  );
}

export default function AdminCosts() {
  return (
    <div className="space-y-8" data-testid="admin-costs-page">
      <div className="rise">
        <h1 className="font-display text-4xl font-bold text-slate-50 sm:text-5xl">AI costs</h1>
        <p className="mt-3 text-sm text-slate-400 md:text-base">What every drawing reading cost in AI usage. Only visible to you.</p>
      </div>
      <AiTotals />
      <Panel title="Model prices (USD per 1M tokens)" testId="ai-prices-panel"><PriceTable /></Panel>
      <Panel title="Readings" testId="readings-panel"><ReadingsTable /></Panel>
    </div>
  );
}
